/**
 * ErrorBoundary
 *
 * Catches uncaught render/lifecycle errors anywhere in the React tree and renders
 * a recoverable fallback instead of unmounting the root.
 *
 * Why this matters: when React unmounts the root, #root becomes empty and the
 * static pre-mount loader in public/index.html reappears (its CSS hides it only
 * while #root is non-empty). The result was a permanent, headerless loading
 * spinner with no way to recover. This boundary keeps #root populated and gives
 * the user a way out. (2026-06-30)
 */

import React from "react";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Uncaught error caught by ErrorBoundary:", error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="w-full h-screen flex flex-col justify-center items-center bg-[#F8F9FA] px-6 text-center">
          <h1 className="text-2xl font-semibold text-[#060606] mb-2">
            Something went wrong
          </h1>
          <p className="text-sm text-[#536787] mb-6 max-w-md">
            The page ran into an unexpected error. Reloading usually fixes it.
          </p>
          <button
            onClick={this.handleReload}
            className="px-6 py-3 rounded-lg bg-[#4F46E5] text-white text-sm font-semibold"
          >
            Reload page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
